/* ANTLITZWERK · resilient lipstick variant → virtual try-on bridge.
   The module is deliberately independent from Horizon's morph lifecycle: the
   button remains visible while a variant is resolving and only its shade URL
   is updated. */

const ROOT_SELECTOR = '[data-antlitzwerk-vto-product]';
const LINK_SELECTOR = '[data-antlitzwerk-vto-link]';
const VARIANT_DATA_SELECTOR = '[data-antlitzwerk-vto-variants]';
const VALID_SHADES = new Set(['rose_samt', 'terracotta_glut']);
const initializedRoots = new WeakMap();
const rememberedShadeByProduct = new Map();
let refreshScheduled = false;

const normalizeId = (value) => String(value ?? '').split('/').pop();

function normalizeText(value) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function inferShade(value) {
  const text = normalizeText(value);
  if (!text) return '';
  if (text.includes('terracotta')) return 'terracotta_glut';
  if (text.includes('rose')) return 'rose_samt';
  return '';
}

function parseVariantShades(root) {
  const script = root.querySelector(VARIANT_DATA_SELECTOR);
  if (!(script instanceof HTMLScriptElement)) return {};

  try {
    const parsed = JSON.parse(script.textContent || '{}');
    return Object.fromEntries(
      Object.entries(parsed).filter(([, shade]) => VALID_SHADES.has(String(shade)))
    );
  } catch {
    return {};
  }
}

function getProductScope(root) {
  return root.closest('product-information, .shopify-section, [data-product-id]') || root.parentElement || document;
}

function getSelectedVariantId(scope) {
  const cartVariant = scope.querySelector?.('form[action*="/cart/add"] [name="id"]');
  if (cartVariant?.value) return normalizeId(cartVariant.value);

  const checkedOption = scope.querySelector?.('variant-picker input:checked[data-variant-id]');
  if (checkedOption?.dataset?.variantId) return normalizeId(checkedOption.dataset.variantId);

  const selectedOption = scope.querySelector?.('variant-picker select option:checked[data-variant-id]');
  if (selectedOption?.dataset?.variantId) return normalizeId(selectedOption.dataset.variantId);

  return '';
}

function getSelectedOptionText(scope) {
  const values = [];

  scope.querySelectorAll?.('variant-picker input:checked').forEach((input) => {
    values.push(input.value, input.getAttribute('aria-label'));
    const label = input.closest('label');
    if (label) values.push(label.textContent);
  });

  scope.querySelectorAll?.('variant-picker select').forEach((select) => {
    const option = select.selectedOptions?.[0];
    values.push(select.value, option?.textContent);
  });

  scope.querySelectorAll?.('[data-testid="variant-option-single-value"], .variant-option__swatch-value').forEach((element) => {
    values.push(element.textContent);
  });

  return values.filter(Boolean).join(' ');
}

function shadeFromUrl(link) {
  try {
    const shade = new URL(link.href, document.baseURI).searchParams.get('shade') || '';
    return VALID_SHADES.has(shade) ? shade : '';
  } catch {
    return '';
  }
}

function preservePreviewParameters(destination) {
  const currentUrl = new URL(window.location.href);
  ['preview_theme_id', '_fd', 'pb'].forEach((parameter) => {
    const value = currentUrl.searchParams.get(parameter);
    if (value !== null && value !== '') destination.searchParams.set(parameter, value);
  });
}

function renderRoot(root, preferred = {}) {
  if (!(root instanceof HTMLElement) || !root.isConnected) return;

  const state = initializedRoots.get(root) || setupRoot(root);
  if (!state) return;

  const { link, variantShades, scope, productId } = state;
  const requestedVariantId = normalizeId(preferred.variantId || getSelectedVariantId(scope));
  const hintedShade = VALID_SHADES.has(preferred.shade) ? preferred.shade : inferShade(preferred.text);
  const selectedTextShade = inferShade(getSelectedOptionText(scope));
  const rememberedShade = rememberedShadeByProduct.get(productId) || '';
  const productShade = VALID_SHADES.has(root.dataset.productShadeKey) ? root.dataset.productShadeKey : '';
  const initialShade = VALID_SHADES.has(root.dataset.initialShadeKey) ? root.dataset.initialShadeKey : '';

  const shade = [
    variantShades[requestedVariantId],
    hintedShade,
    selectedTextShade,
    rememberedShade,
    state.lastShade,
    shadeFromUrl(link),
    productShade,
    initialShade,
    'rose_samt'
  ].find((candidate) => VALID_SHADES.has(candidate));

  state.lastShade = shade;
  rememberedShadeByProduct.set(productId, shade);

  // Once Liquid has decided that this is the lipstick product, temporary
  // Horizon variant states must never remove the customer action.
  root.hidden = false;
  link.hidden = false;
  root.removeAttribute('hidden');
  link.removeAttribute('hidden');
  root.dataset.activeShadeKey = shade;

  const destination = new URL(root.dataset.tryOnBaseUrl || link.href, document.baseURI);
  destination.searchParams.set('shade', shade);
  preservePreviewParameters(destination);
  link.href = `${destination.pathname}${destination.search}${destination.hash}`;
  link.setAttribute(
    'aria-label',
    shade === 'terracotta_glut' ? root.dataset.ariaTerracotta : root.dataset.ariaRose
  );
}

function setupRoot(root) {
  if (!(root instanceof HTMLElement)) return null;
  const existing = initializedRoots.get(root);
  if (existing) return existing;

  const link = root.querySelector(LINK_SELECTOR);
  if (!(link instanceof HTMLAnchorElement)) return null;

  const productId = normalizeId(root.dataset.productId) || 'rosenanmut';
  const state = {
    root,
    link,
    scope: getProductScope(root),
    productId,
    variantShades: parseVariantShades(root),
    lastShade: rememberedShadeByProduct.get(productId)
      || (VALID_SHADES.has(root.dataset.initialShadeKey) ? root.dataset.initialShadeKey : '')
      || shadeFromUrl(link)
      || 'rose_samt'
  };

  initializedRoots.set(root, state);
  renderRoot(root);
  return state;
}

function rootsForProduct(productId = '') {
  const normalizedProductId = normalizeId(productId);
  return [...document.querySelectorAll(ROOT_SELECTOR)].filter((root) => {
    if (!(root instanceof HTMLElement)) return false;
    return !normalizedProductId || normalizeId(root.dataset.productId) === normalizedProductId;
  });
}

function refreshRoots(preferred = {}, productId = '') {
  rootsForProduct(productId).forEach((root) => renderRoot(root, preferred));
}

function scheduleRefresh(preferred = {}, productId = '') {
  const run = () => refreshRoots(preferred, productId);
  queueMicrotask(run);
  requestAnimationFrame(run);
  window.setTimeout(run, 80);
  window.setTimeout(run, 320);
}

function optionContext(target) {
  if (!(target instanceof Element)) return null;
  const input = target.closest('variant-picker input, variant-picker option, variant-picker select, .variant-option__button-label');
  if (!input) return null;

  const picker = input.closest('variant-picker');
  const productId = picker?.dataset?.productId || '';
  const source = input.matches('select') ? input.selectedOptions?.[0] : input;
  const text = [
    source?.value,
    source?.getAttribute?.('aria-label'),
    source?.textContent,
    source?.closest?.('label')?.textContent
  ].filter(Boolean).join(' ');
  const shade = inferShade(text);
  const variantId = source?.dataset?.variantId || input.dataset?.variantId || '';

  return { productId, shade, text, variantId };
}

function productBelongs(root, productId) {
  return !productId || normalizeId(root.dataset.productId) === normalizeId(productId);
}

function handleVariantEvent(event) {
  const detail = event.detail || {};
  const eventProductId = event.product?.id
    || detail.product?.id
    || detail.productId
    || detail.resource?.product?.id
    || detail.resource?.product_id
    || '';
  const variant = detail.variant || event.variant || {};
  const variantId = variant.id || detail.variantId || detail.id || detail.resource?.id || '';
  const text = [variant.title, variant.option1, variant.option2, variant.option3, detail.value].filter(Boolean).join(' ');
  const shade = inferShade(text);

  rootsForProduct(eventProductId).forEach((root) => {
    if (!productBelongs(root, eventProductId)) return;
    const state = setupRoot(root);
    const mapped = state?.variantShades?.[normalizeId(variantId)] || shade;
    if (VALID_SHADES.has(mapped)) rememberedShadeByProduct.set(state.productId, mapped);
  });
  scheduleRefresh({ variantId, shade, text }, eventProductId);

  const eventPromise = event.promise || detail.promise;
  if (eventPromise && typeof eventPromise.then === 'function') {
    eventPromise.then((payload) => {
      const resolvedDetail = payload?.detail || {};
      const resolvedVariant = payload?.variant || resolvedDetail.variant || {};
      const resolvedProductId = resolvedDetail.productId
        || resolvedDetail.resource?.product_id
        || resolvedDetail.resource?.product?.id
        || payload?.product?.id
        || eventProductId;
      const resolvedVariantId = resolvedVariant.id
        || payload?.variantId
        || resolvedDetail.variantId
        || resolvedDetail.resource?.id
        || '';
      const resolvedText = [
        resolvedVariant.title,
        resolvedVariant.option1,
        resolvedVariant.option2,
        resolvedVariant.option3
      ].filter(Boolean).join(' ');
      scheduleRefresh({
        variantId: resolvedVariantId,
        shade: inferShade(resolvedText),
        text: resolvedText
      }, resolvedProductId);
    }).catch(() => undefined);
  }
}

function initializeWithin(root = document) {
  if (root instanceof Element && root.matches(ROOT_SELECTOR)) setupRoot(root);
  root.querySelectorAll?.(ROOT_SELECTOR).forEach(setupRoot);
}

initializeWithin();

document.addEventListener('change', (event) => {
  const context = optionContext(event.target);
  if (!context) return;
  if (VALID_SHADES.has(context.shade)) {
    rootsForProduct(context.productId).forEach((root) => {
      const state = setupRoot(root);
      if (state) rememberedShadeByProduct.set(state.productId, context.shade);
    });
  }
  scheduleRefresh(context, context.productId);
}, true);

document.addEventListener('click', (event) => {
  const context = optionContext(event.target);
  if (!context) return;
  if (VALID_SHADES.has(context.shade)) {
    rootsForProduct(context.productId).forEach((root) => {
      const state = setupRoot(root);
      if (state) rememberedShadeByProduct.set(state.productId, context.shade);
    });
  }
  scheduleRefresh(context, context.productId);
}, true);

document.addEventListener('variant:change', handleVariantEvent, true);
document.addEventListener('shopify:product:select', handleVariantEvent, true);

const observer = new MutationObserver((records) => {
  let shouldRefresh = false;
  for (const record of records) {
    record.addedNodes.forEach((node) => {
      if (!(node instanceof Element)) return;
      initializeWithin(node);
      if (node.matches(ROOT_SELECTOR) || node.querySelector(ROOT_SELECTOR)) shouldRefresh = true;
    });
  }
  if (shouldRefresh) scheduleRefresh();
});
observer.observe(document.documentElement, { childList: true, subtree: true });
